import { captureException } from '@sentry/react';
import moment from 'moment';
import { useCallback } from 'react';

import { VisitInfo, authService } from 'src/services/auth';
import { AutomedClinicAvailabilityTimeModel, AutomedDoctorAvailabilityTimeModel } from 'src/services/availability';
import { MedicareCardStatus } from 'src/services/patient';
import { openWindowViaLink } from 'src/utils/html';
import { toAutomedDoctorAvailabilityTimeModel } from 'src/utils/mapAutomedTimeTypes';

export type openAppOptions = {
    useSameWindow: boolean;
    customerId?: string | number;
};

export function useOpenApp({ useSameWindow = false, customerId }: openAppOptions = { useSameWindow: false }) {
    const openApp = useCallback(
        async (
            clinicId: string | number,
            doctorUserId: string | number,
            date: Date,
            appointmentTypeId: string | number,
            patient: { name: string; dob: string },
            timeSlot: AutomedClinicAvailabilityTimeModel | AutomedDoctorAvailabilityTimeModel,
            visitInfo: VisitInfo,
            medicareStatus: MedicareCardStatus,
            comment?: string,
            telehealth?: { timeslotId: number },
            telehealthMedicareExemptions?: string[]
        ) => {
            let newWindow: Window | null = null;
            if (!useSameWindow) {
                newWindow = window.open();
            }
            try {
                // const { access_token } = await getBookingToken();
                const query = new URLSearchParams({
                    dateTime: moment(date).toISOString(),
                    doctor: doctorUserId.toString(),
                    clinic: clinicId.toString(),
                    appointment: appointmentTypeId.toString(),
                    _booking: authService.Token.jwt,
                    patientName: patient.name,
                    patientDob: patient.dob,
                    new: visitInfo.isNewPatient.toString(),
                    timeslotId: telehealth?.timeslotId?.toString() ?? '',
                    medicareStatus,
                });
                if (comment) {
                    query.append('comment', comment);
                }
                const mappedTimeSlot = toAutomedDoctorAvailabilityTimeModel(timeSlot);
                for (const [key, value] of Object.entries(mappedTimeSlot)) {
                    if (key && value) {
                        query.append(`timeSlot.${key}`, value);
                    }
                }
                query.append('visit.isNewPatient', visitInfo.isNewPatient.toString());
                if (visitInfo.bannedLevel) {
                    query.append('visit.bannedLevel', visitInfo.bannedLevel);
                }
                if (visitInfo.lastF2FDate) {
                    query.append('visit.lastF2FDate', visitInfo.lastF2FDate);
                }
                if (visitInfo.lastSeenDid) {
                    query.append('visit.lastSeenDid', visitInfo.lastSeenDid.toString());
                }
                if (customerId) {
                    query.append('customerId', customerId.toString());
                }
                for (const exemption of telehealthMedicareExemptions ?? []) {
                    query.append('telehealthMedicareExemptions', exemption);
                }
                let url = `${window.location.origin}/app?${query}`;

                if (!useSameWindow) {
                    if (newWindow) {
                        newWindow.location = url;
                    }

                    if (!newWindow || newWindow.closed) {
                        query.append(
                            'fallback',
                            'xyxyxyxyxyxy'.replace(/[xy]/g, (n) => {
                                const e = (Math.random() * 16) | 0;
                                return (n == 'x' ? e : (e & 3) | 8).toString(16);
                            })
                        );
                        url = `${window.location.origin}/app?${query}`;

                        console.debug(`Attempting to open booking app with fallback ?${query}`);

                        // Fallback to open via link
                        openWindowViaLink(url);
                        captureException(`Attempting to open booking app with fallback.`, {
                            level: 'warning',
                            extra: {
                                query: Object.fromEntries(query),
                            },
                        });
                    }
                } else {
                    window.location.href = url;
                }
            } catch (e) {
                console.error(e);
                newWindow?.close();
                captureException(e);
            }
        },
        [customerId, useSameWindow]
    );

    return { openApp };
}
